import { ROLES } from "./catalogue.js";
/**
 * Who may do what *to whom*.
 *
 * `catalogue.ts` answers "may this role perform this action?" — a question
 * about a verb. It cannot answer "may this person perform it on that person?",
 * because that is a question about two subjects and a ranking between them, and
 * a ranking inside the permission map would give every permission an ordering
 * it does not have. Hence a separate module, and hence the catalogue is
 * untouched.
 *
 * It lives in this package rather than in `@aeris/auth` for the reason ADR-0013
 * gives for the split: this package is the one both the API and the browser
 * import, so the screen hides the roles it may not grant using the same
 * function the server refuses them with, and the two cannot drift. The server
 * still enforces independently — the browser copy is display, never a control.
 *
 * Every rule is a named exported function rather than an `if` inside a handler.
 * A rule nobody can point at is a rule nobody can review. Master Specification
 * section 28.
 */
/**
 * Lower is more authority. Derived from the declaration order in
 * `@aeris/permissions`, which is already most-to-least privileged, so the two
 * cannot drift into disagreeing.
 */
const RANK = Object.fromEntries(ROLES.map((role, index) => [role, index]));
/** Exposed for tests and for rendering; the rules below are the API. */
export const roleRank = (role) => RANK[role];
/**
 * RULE — NO ROLE ESCALATION.
 *
 * Nobody may grant a role above their own.
 *
 * Without this, `identity.user.write` is not the permission to administer
 * users, it is the permission to become an owner: an admin invites an account,
 * grants it `owner`, signs in as it, and the role boundary has cost an attacker
 * two requests. The permission catalogue cannot express this, because from its
 * point of view "change a role" is one action and every role is just a value.
 *
 * Equal is allowed: an admin may appoint another admin. That is delegation, not
 * escalation — it grants nothing the actor did not already hold.
 */
export const canGrantRole = (actor, granted) => RANK[actor] <= RANK[granted];
/**
 * RULE — NO ACTING UPWARDS.
 *
 * Nobody may act on a member who outranks them.
 *
 * The sibling of the rule above, closing the same hole from the other side. An
 * admin who cannot grant `owner` but *can* disable the owner has not been
 * stopped from taking the tenant, only slowed down: they lock the owner out and
 * are then the most senior person left.
 *
 * Equal ranks may act on each other, deliberately. Two owners are peers, and a
 * model where a compromised owner cannot be removed by their co-owner is worse
 * than one where they can — the last-owner rule is what stops that becoming a
 * way to empty the tenant.
 */
export const canActOnMember = (actor, subject) => RANK[actor] <= RANK[subject];
//# sourceMappingURL=authority.js.map